import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router';

import { cn } from '@/lib/cn';

/**
 * The title block every home page band opens with: heading on the left, and
 * where there is somewhere further to go, a quiet link on the right that drops
 * under the heading on a phone rather than squeezing beside it.
 *
 * `id` is here so the section around this can point `aria-labelledby` at the
 * heading instead of repeating it as an `aria-label`.
 */
export function SectionHeading({
  id,
  heading,
  subheading,
  link,
  className,
}: {
  id?: string;
  heading: string;
  subheading?: string;
  link?: { label: string; to: string };
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-8',
        className,
      )}
    >
      <div>
        <h2
          id={id}
          className="text-balance text-2xl font-bold tracking-tight text-ink sm:text-3xl"
        >
          {heading}
        </h2>
        {subheading ? (
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-muted sm:text-base">
            {subheading}
          </p>
        ) : null}
      </div>

      {link ? (
        <Link
          to={link.to}
          className="group inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg py-1 text-sm font-semibold text-brand-600 transition-colors hover:text-brand-700 sm:self-auto"
        >
          {link.label}
          <ArrowRight
            className="size-4 transition-transform group-hover:translate-x-0.5 rtl-flip"
            aria-hidden
          />
        </Link>
      ) : null}
    </div>
  );
}
